import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2, Printer, AlertCircle } from 'lucide-react';
import { BillingInfo, PaymentRecord } from '../lib/billing-hooks';
/**
 * InvoicePage — public receipt viewer.
 *
 * URL: /invoice/:paymentId
 *
 * Renders a thermal-printer-friendly comprobante for a given payment.
 *
 * MULTI-TENANT SAFETY: every fetch is keyed by `payment.restaurant_id`
 * (which comes from the payment row itself, not from any URL param or
 * tenant context). The billing info, the restaurant logo, and the
 * mesa label are all loaded with `.eq('restaurant_id', pay.restaurant_id)`
 * or by following its FK ids. No cross-tenant data ever reaches this
 * page.
 */
export function InvoicePage() {
  const {
    paymentId
  } = useParams<{
    paymentId: string;
  }>();
  const [payment, setPayment] = useState<PaymentRecord | null>(null);
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [tableLabel, setTableLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!paymentId) return;
      try {
        // 1) Payment row — authoritative source for restaurant_id
        const {
          data: pay,
          error: payErr
        } = await supabase.from('payments').select('*').eq('id', paymentId).maybeSingle();
        if (payErr) throw payErr;
        if (!pay) throw new Error('Factura no encontrada');
        // 2) Billing info — scoped to this payment's restaurant_id
        const {
          data: billing,
          error: bErr
        } = await supabase.from('restaurant_billing_info').select('*').eq('restaurant_id', pay.restaurant_id).maybeSingle();
        if (bErr) throw bErr;
        if (!billing) throw new Error('Datos del comercio no disponibles');
        // 3) Restaurant logo — scoped to the same restaurant_id. Non-fatal
        //    on failure; we just won't show the logo block.
        let logo: string | null = null;
        try {
          const {
            data: rest
          } = await supabase.from('restaurants').select('logo_url').eq('id', pay.restaurant_id).maybeSingle();
          if (rest && (rest as any).logo_url) {
            logo = (rest as any).logo_url as string;
          }
        } catch {
          logo = null;
        }
        // 4) Mesa label — only when the payment is linked to a table.
        //    `tables` rows have their own restaurant_id, so resolving by
        //    primary key here is tenant-safe by construction.
        let tLabel: string | null = null;
        if (pay.table_id) {
          const {
            data: tbl
          } = await supabase.from('tables').select('table_number, table_name').eq('id', pay.table_id).maybeSingle();
          if (tbl) tLabel = tbl.table_name || `Mesa ${tbl.table_number}`;
        }
        if (mounted) {
          setPayment(pay as PaymentRecord);
          setInfo(billing as BillingInfo);
          setLogoUrl(logo);
          setTableLabel(tLabel);
        }
      } catch (err: any) {
        if (mounted) setError(err.message || 'Error al cargar la factura');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [paymentId]);
  const fmt = (n: number) => new Intl.NumberFormat('es-CR', {
    style: 'currency',
    currency: 'CRC',
    minimumFractionDigits: 0
  }).format(n);
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100" data-id="element-7093">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" data-id="element-7094" />
      </div>;
  }
  if (error || !payment || !info) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6" data-id="element-7095">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-lg" data-id="element-7096">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" data-id="element-7097" />
          <h1 className="text-xl font-black text-gray-900 mb-1" data-id="element-7098">
            Factura no disponible
          </h1>
          <p className="text-sm text-gray-500" data-id="element-7099">
            {error || 'No se pudo cargar el documento.'}
          </p>
        </div>
      </div>;
  }
  // ─── Derived display values ───────────────────────────────────────
  const date = new Date(payment.created_at).toLocaleString('es-CR', {
    dateStyle: 'short',
    timeStyle: 'short'
  });
  const consecStr = String(payment.consecutive_number).padStart(10, '0');
  const methodLabel = payment.method === 'cash' ? 'Efectivo' : payment.method === 'sinpe' ? 'SINPE Móvil' : 'Tarjeta';
  const docTitle = info.regime === 'simplificado' ? 'COMPROBANTE DE PAGO' : 'FACTURA ELECTRÓNICA';
  const regimeLabel = info.regime === 'simplificado' ? 'Régimen Simplificado' : info.regime === 'tradicional' ? 'Régimen Tradicional' : info.regime;
  return <div className="min-h-screen bg-gray-100 py-6 px-4 print:bg-white print:p-0" data-id="element-7100">
      <style data-id="element-7101">{`
        @media print {
          @page { margin: 6mm; size: 80mm auto; }
          body { background: white !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="max-w-sm mx-auto" data-id="element-7102">
        {/* Action bar (hidden when printing) */}
        <div className="no-print flex gap-2 mb-4" data-id="element-7103">
          <button onClick={() => window.print()} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-900 text-white font-bold text-sm shadow-lg hover:bg-gray-800 transition-colors" data-id="element-7104">
            <Printer className="w-4 h-4" data-id="element-7105" />
            Imprimir / Guardar PDF
          </button>
        </div>

        <p className="no-print text-xs text-gray-500 text-center mb-4 leading-relaxed" data-id="element-7106">
          💡 En el diálogo de impresión, elige{' '}
          <strong data-id="element-7107">"Guardar como PDF"</strong> para descargar el comprobante, o
          seleccioná tu impresora térmica de 80&nbsp;mm.
        </p>

        {/* ───────── Receipt body ───────── */}
        <div className="bg-white p-5 shadow-md font-mono text-gray-900 border border-gray-200 print:border-0 print:shadow-none" data-id="element-7108">
          {/* Logo (when available) — matches the physical receipt: small,
           centered, square-ish, with a subtle border for printer
           contrast. Falls back gracefully when logo_url is missing. */}
          {logoUrl && <div className="flex justify-center mb-3" data-id="element-7109">
              <div className="w-20 h-20 rounded-md overflow-hidden border border-gray-200 bg-white flex items-center justify-center" data-id="element-7110">
                <img src={logoUrl} alt={info.business_name} className="w-full h-full object-contain"
            // Hide the image element if it fails to load — the
            // surrounding box still adds visual weight to the header.
            onError={e => {
              ;
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }} data-id="element-7111" />
              </div>
            </div>}

          {/* Comercial / brand name (large, centered) */}
          <h1 className="text-base font-black text-center tracking-wide mb-0.5" data-id="element-7112">
            {info.business_name}
          </h1>

          {/* Legal owner name (only for personas físicas with legal_name
           configured — e.g. Régimen Simplificado). Backwards compatible:
           when null/empty, this entire line is skipped. */}
          {info.legal_name && <p className="text-[11px] text-center font-bold uppercase tracking-wider mb-2" data-id="element-7113">
              {info.legal_name}
            </p>}

          {/* Contact / legal block — compact, centered, mirrors the
           physical receipt's "address + phone + email" layout. */}
          <div className="text-[11px] text-center leading-snug mb-3 space-y-0.5" data-id="element-7114">
            <div data-id="element-7115">
              <strong data-id="element-7116">
                Céd. {info.legal_id_type === 'fisica' ? '' : 'Jur. '}
                {info.legal_id}
              </strong>
            </div>
            {info.phone && <div data-id="element-7117">Tel. {info.phone}</div>}
            {info.address && <div data-id="element-7118">{info.address}</div>}
            {info.email && <div className="break-all" data-id="element-7119">{info.email}</div>}
          </div>

          {/* Document type + consecutive — visually anchored as a "stamp" */}
          <div className="border-y border-dashed border-gray-400 py-2 my-2" data-id="element-7120">
            <p className="text-[11px] font-black tracking-wider text-center mb-0.5" data-id="element-7121">
              {docTitle}
            </p>
            <p className="text-center font-mono text-xs tabular-nums" data-id="element-7122">
              {consecStr}
              {payment.is_partial && <span className="ml-1 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-[9px] font-bold" data-id="element-7123">
                  PARCIAL
                </span>}
            </p>
            <p className="text-[10px] text-center text-gray-600 mt-0.5" data-id="element-7124">
              {date}
            </p>
            {tableLabel && <p className="text-[10px] text-center text-gray-700 mt-0.5" data-id="element-7125">
                <strong data-id="element-7126">{tableLabel}</strong>
              </p>}
          </div>

          {/* Items table — column widths tuned for 80mm thermal paper */}
          <table className="w-full text-[11px] my-2" data-id="element-7127">
            <thead data-id="element-7128">
              <tr className="border-b border-gray-900 uppercase text-[10px]" data-id="element-7129">
                <td className="pb-1 w-8" data-id="element-7130">Cant.</td>
                <td className="pb-1" data-id="element-7131">Descripción</td>
                <td className="pb-1 text-right whitespace-nowrap" data-id="element-7132">
                  Monto total
                </td>
              </tr>
            </thead>
            <tbody data-id="element-7133">
              {(payment.items_json || []).map((it, idx) => <tr key={idx} className="align-top" data-id="element-7134">
                  <td className="font-bold pt-1.5" data-id="element-7135">{it.quantity}</td>
                  <td className="pr-2 pt-1.5" data-id="element-7136">
                    <div className="leading-tight" data-id="element-7137">{it.item_name}</div>
                    {it.notes && <div className="text-[10px] text-gray-500 italic mt-0.5" data-id="element-7138">
                        {it.notes}
                      </div>}
                  </td>
                  <td className="text-right tabular-nums whitespace-nowrap pt-1.5" data-id="element-7139">
                    {fmt(it.line_total)}
                  </td>
                </tr>)}
            </tbody>
          </table>

          {/* Total — bold and visually dominant, matching the physical
           receipt's "Total ₡4,400.00" style. */}
          <div className="border-t-2 border-gray-900 pt-2 mt-2" data-id="element-7140">
            <div className="flex justify-between items-baseline" data-id="element-7141">
              <span className="text-lg font-black" data-id="element-7142">Total</span>
              <span className="text-lg font-black tabular-nums" data-id="element-7143">
                {fmt(payment.amount)}
              </span>
            </div>
          </div>

          {/* Payment metadata — Moneda + F. Pago */}
          <div className="border-t border-dashed border-gray-400 pt-2 mt-3 space-y-0.5 text-[11px]" data-id="element-7144">
            <div className="flex justify-between" data-id="element-7145">
              <span data-id="element-7146">Moneda</span>
              <strong data-id="element-7147">CRC</strong>
            </div>
            <div className="flex justify-between" data-id="element-7148">
              <span data-id="element-7149">F. Pago</span>
              <strong data-id="element-7150">{methodLabel}</strong>
            </div>
          </div>

          {/* Regime + footer note + legal disclaimer */}
          <p className="text-[11px] mt-3 font-medium" data-id="element-7151">{regimeLabel}</p>
          {info.footer_note && <p className="text-center text-[12px] mt-4 mb-1 font-medium text-gray-800" data-id="element-7152">
              {info.footer_note}
            </p>}
          <p className="text-center text-[9px] mt-3 text-gray-500 leading-tight" data-id="element-7153">
            {info.regime === 'simplificado' ? 'Documento equivalente — no constituye factura electrónica.' : 'Documento autorizado por la DGT — Costa Rica.'}
          </p>
          <p className="text-center text-[9px] mt-1 text-gray-400" data-id="element-7154">
            Powered by NOIZA
          </p>
        </div>
      </div>
    </div>;
}